import { ChangeDetectorRef, Component, Injector, computed, effect, inject, OnInit, AfterViewInit } from '@angular/core';
import { catchError, takeUntil } from 'rxjs/operators';
import { of } from 'rxjs';
import { OveraLayoutComponent } from '@overa/base';
import { DeviceDetectorService, DeviceType, IMenu, LayoutModeService } from '@overa/shared';
import { OveraMenuItem } from '@overa/components/menubar';

import { ConsoleCapability, ConsoleCapabilityValue } from '../../../core/console/console.model';
import { sessionPlatform } from '../../../core/platform/platform-session';
import { ConsoleService } from '../../../core/console/console.service';
import { IdentityLinksService } from '../../../core/identity/identity-links.service';
import { PlatformCapabilitiesService } from '../../../core/platform/platform-capabilities.service';
import { PlatformService } from '../../../core/platform/platform.service';
import { switchPlatformUrl } from '../../../core/platform/platform-url';
import { AiDrawerSpaceService } from '../../../core/ai-drawer/ai-drawer-space.service';
import { AiContextSection, AiNavigationTarget } from '@overa/ai';
import { NOTIFICATION_TYPE, NotificationComponent, NotificationService } from '@overa/notification';
import { WIRING_TAB_LABELS, WIRING_TABS } from '../../../modules/platforms/platform-tabs';

/**
 * Menú de la **plataforma** abierta: lo que se administra dentro de un producto.
 *
 * Los `uiUrl` son relativos a propósito. La plataforma va en la dirección (`/p/{code}/...`) y la
 * antepone `platformMenu()`: escribirla aquí obligaría a duplicar el árbol por producto, y un menú con
 * la plataforma equivocada es la forma silenciosa de administrar la que no era.
 */
export const BACKOFFICE_MENU: IMenu[] = [
  {
    // The platform's landing page: what state it is in and shortcuts to the usual jobs.
    id: 'overview',
    name: 'BACKOFFICE.MENU.OVERVIEW',
    icon: 'fa fa-home',
    uiUrl: 'overview',
    order: 0,
    isVisible: true,
  },
  {
    id: 'admin',
    name: 'BACKOFFICE.MENU.ADMIN',
    icon: 'fa fa-cog',
    order: 1,
    isVisible: true,
    // No 'dynamic-script' entry: dynamic front-end work goes through Dynamic views and dynamic back-end
    // work through Dynamic Studio. The route stays alive for links already handed out.
    children: [
      {
        id: 'dynamic-view',
        name: 'BACKOFFICE.MENU.DYNAMIC_VIEW',
        icon: 'fa fa-th-large',
        uiUrl: 'dynamic-view',
        order: 2,
        isVisible: true,
      },
      {
        id: 'dynamic-studio',
        name: 'BACKOFFICE.MENU.DYNAMIC_STUDIO',
        icon: 'fa fa-flask',
        uiUrl: 'dynamic-studio',
        order: 3,
        isVisible: true,
      },
      {
        id: 'source-control',
        name: 'BACKOFFICE.MENU.SOURCE_CONTROL',
        icon: 'fa fa-code-branch',
        uiUrl: 'source-control',
        order: 4,
        isVisible: true,
      },
      {
        // Servicio interno Reporting (`reporting-api` o `reportingApi`): la pantalla avisa si la plataforma no lo tiene conectado.
        id: 'reports',
        name: 'BACKOFFICE.MENU.REPORTS',
        icon: 'fa fa-file-alt',
        uiUrl: 'reports',
        order: 5,
        isVisible: true,
      },
      {
        // De toda la plataforma, no de un microservicio: las ventanas viven en Identity (ver
        // docs/MANTENIMIENTO-PLATAFORMA.md). Por eso no está en MENU_SURFACES: no depende de la sonda.
        id: 'maintenance',
        name: 'BACKOFFICE.MENU.PLATFORM_MAINTENANCE',
        icon: 'fa fa-tools',
        uiUrl: 'maintenance',
        order: 6,
        isVisible: true,
      },
      {
        // Como el mantenimiento: de la plataforma y en Identity, fuera de MENU_SURFACES.
        id: 'themes',
        name: 'BACKOFFICE.MENU.PLATFORM_THEMES',
        icon: 'fa fa-palette',
        uiUrl: 'themes',
        order: 7,
        isVisible: true,
      },
    ],
  },
  {
    // Administración del microservicio de IA. Sección propia y no hija de "Administración"
    // porque mezcla cosas de perfiles distintos: consumo y planes los mira quien lleva costes,
    // prompts y enrutado quien lleva el producto, y la memoria contiene datos personales.
    //
    // Aquí solo está la mitad que depende de **inquilinos**: consumo, planes, memoria, el flujo
    // resuelto y lo que cada inquilino cambia encima del catálogo. La definición global del servicio
    // —que es uno solo para todas las plataformas— vive en el primer nivel, en `AI_SYSTEM_MENU`.
    id: 'ai-admin',
    name: 'BACKOFFICE.MENU.AI',
    icon: 'fa fa-brain',
    order: 2,
    isVisible: true,
    children: [
      {
        id: 'ai-usage',
        name: 'BACKOFFICE.MENU.AI_USAGE',
        icon: 'fa fa-chart-line',
        uiUrl: 'ai-admin/usage',
        order: 1,
        isVisible: true,
      },
      {
        id: 'ai-flow',
        name: 'BACKOFFICE.MENU.AI_FLOW',
        icon: 'fa fa-project-diagram',
        uiUrl: 'ai-admin/flow',
        order: 2,
        isVisible: true,
      },
      {
        id: 'ai-agents',
        name: 'BACKOFFICE.MENU.AI_AGENTS',
        icon: 'fa fa-robot',
        uiUrl: 'ai-admin/agents',
        order: 3,
        isVisible: true,
      },
      {
        id: 'ai-skills',
        name: 'BACKOFFICE.MENU.AI_SKILLS',
        icon: 'fa fa-puzzle-piece',
        uiUrl: 'ai-admin/skills',
        order: 4,
        isVisible: true,
      },
      {
        id: 'ai-tiers',
        name: 'BACKOFFICE.MENU.AI_TIERS',
        icon: 'fa fa-layer-group',
        uiUrl: 'ai-admin/tiers',
        order: 5,
        isVisible: true,
      },
      {
        id: 'ai-mcp-tools',
        name: 'BACKOFFICE.MENU.AI_MCP_TOOLS',
        icon: 'fa fa-plug',
        uiUrl: 'ai-admin/mcp-tools',
        order: 6,
        isVisible: true,
      },
      {
        id: 'ai-assist-tasks',
        name: 'BACKOFFICE.MENU.AI_ASSIST_TASKS',
        icon: 'fa fa-magic',
        uiUrl: 'ai-admin/assist-tasks',
        order: 7,
        isVisible: true,
      },
      {
        id: 'ai-tenants',
        name: 'BACKOFFICE.MENU.AI_TENANTS',
        icon: 'fa fa-building',
        uiUrl: 'ai-admin/tenants',
        order: 8,
        isVisible: true,
      },
      {
        id: 'ai-routing',
        name: 'BACKOFFICE.MENU.AI_ROUTING',
        icon: 'fa fa-random',
        uiUrl: 'ai-admin/routing',
        order: 9,
        isVisible: true,
      },
      {
        id: 'ai-assets',
        name: 'BACKOFFICE.MENU.AI_ASSETS',
        icon: 'fa fa-file-alt',
        uiUrl: 'ai-admin/assets',
        order: 10,
        isVisible: true,
      },
      {
        id: 'ai-memory',
        name: 'BACKOFFICE.MENU.AI_MEMORY',
        icon: 'fa fa-database',
        uiUrl: 'ai-admin/memory',
        order: 11,
        isVisible: true,
      },
    ],
  },
];

/**
 * Seguridad: roles, permisos, usuarios y menús **del tenant que se elija**.
 *
 * Sección aparte de "Administración" porque no administra el BackOffice sino **el acceso a las demás
 * aplicaciones**: lo que se cambia aquí decide quién entra en Activity. Y se añade solo para
 * administradores, no con `isVisible: true` fijo como el resto del menú: las rutas ya están detrás de
 * `SecurityAdminGuard`, y ofrecer una entrada que lleva a "acceso denegado" es peor que no ofrecerla.
 *
 * El mantenimiento de tenants NO está aquí: ver `TENANTS_MENU`.
 *
 * ## OJO: esta sección cuelga de la plataforma, pero su ámbito NO es la plataforma
 *
 * Estas pantallas son las de `@overa/security` y van contra **Identity**, que hoy es el mismo para
 * las tres plataformas. El editor de menús pide `GET {identityBaseUrl}/menu` sin parámetro de
 * plataforma ni de aplicación, y `MenuItem` es una tabla **global**: lo único que se acota por tenant
 * es a quién se le enseña cada entrada (`MenuItemTenant`), no la entrada en sí. Resultado: entrando
 * por Harley se ven exactamente las mismas entradas que entrando por Activity.
 *
 * No es un descuido del shell: **no hay por dónde acotarlo todavía**. El eje `Platform` de Identity
 * existe solo en la rama `migration/openiddict` y ahí está inerte. Decisión tomada (2026-09-21):
 * esperar a ese eje en vez de duplicar en el BackOffice un mapa plataforma→inquilinos que se
 * desfasaría. Cuando llegue, el recorte se deriva sin tocar `MenuItem`: los menús se acotan por
 * inquilino y los inquilinos pasan a tener plataforma (`Tenant.Platform`).
 */
export const SECURITY_MENU: IMenu = {
  id: 'security',
  name: 'BACKOFFICE.MENU.SECURITY',
  icon: 'fa fa-shield-alt',
  order: 3,
  isVisible: true,
  children: [
    {
      id: 'security-roles',
      name: 'BACKOFFICE.MENU.SECURITY_ROLES',
      icon: 'fa fa-user-shield',
      uiUrl: 'security/roles',
      order: 1,
      isVisible: true,
    },
    {
      id: 'security-users',
      name: 'BACKOFFICE.MENU.SECURITY_USERS',
      icon: 'fa fa-users',
      uiUrl: 'security/users',
      order: 2,
      isVisible: true,
    },
    {
      id: 'security-menu',
      name: 'BACKOFFICE.MENU.SECURITY_MENU',
      icon: 'fa fa-bars',
      uiUrl: 'security/menu',
      order: 3,
      isVisible: true,
    },
  ],
};

/**
 * Mantenimiento de tenants: entrada propia, fuera de "Seguridad".
 *
 * Va aparte porque es lo único de esa familia que **no se hace dentro de un tenant**: es la lista
 * global, da de alta y configura tenants. Como pestaña de la zona de seguridad obligaba a explicar en
 * pantalla que allí el selector de tenant no manda nada — y un texto que avisa de una incoherencia es
 * peor que no tener la incoherencia.
 *
 * Entrada de primer nivel sin hijos: el sidebar navega con `uiUrl` (lo convierte a `url` en
 * `convertMenuToOveraMenuItem`) y `onClick` navega igual con hijos o sin ellos.
 *
 * Le aplica lo mismo que a `SECURITY_MENU`: cuelga de la plataforma pero lista los inquilinos de
 * Identity **sin recortar por ella**, porque el eje todavía no existe. Ver el comentario de allí.
 */
export const TENANTS_MENU: IMenu = {
  id: 'tenants',
  name: 'BACKOFFICE.MENU.TENANTS',
  icon: 'fa fa-building',
  uiUrl: 'tenants',
  order: 4,
  isVisible: true,
};

/**
 * Quién puede usar cada aplicación de la plataforma (docs/GESTION-APLICACIONES.md, «Acceso de usuarios»).
 * Entrada propia, junto a «Organizaciones», para quien administra la plataforma: los operadores de la
 * consola y sus administradores globales. Las piezas OAuth de cada aplicación siguen en el catálogo.
 */
export const APPLICATIONS_MENU: IMenu = {
  id: 'applications',
  name: 'BACKOFFICE.MENU.APPLICATIONS',
  icon: 'fa fa-th',
  uiUrl: 'applications',
  order: 5,
  isVisible: true,
};

/**
 * Administración del **sistema** de IA: la definición global del microservicio.
 *
 * El servicio de IA es uno solo para todas las plataformas, así que su configuración, su catálogo de
 * agentes, skills, tiers y herramientas, sus reglas de enrutado y sus activos de prompt no son de
 * ninguna plataforma en concreto: son del sistema. Lo que cada **inquilino** cambia encima sí es de
 * una plataforma, y se administra desde ella (`BACKOFFICE_MENU`).
 *
 * Son las mismas pantallas en los dos sitios. Lo único que cambia es el modo, y lo deduce
 * `AiScopeService` de si hay plataforma abierta: aquí arriba no hay selector de inquilino.
 *
 * Los `uiUrl` son absolutos porque este menú no cuelga de ninguna plataforma.
 */
export const AI_SYSTEM_MENU: IMenu = {
  id: 'ai-system',
  name: 'BACKOFFICE.MENU.AI',
  icon: 'fa fa-brain',
  order: 5,
  isVisible: true,
  children: [
    {
      id: 'ai-system-config',
      name: 'BACKOFFICE.MENU.AI_CONFIG',
      icon: 'fa fa-sliders-h',
      uiUrl: '/ai/config',
      order: 1,
      isVisible: true,
    },
    {
      // Planes de cambio de la administración de plataformas asistida por IA: revisar/aprobar/aplicar.
      id: 'ai-system-change-plans',
      name: 'BACKOFFICE.MENU.AI_CHANGE_PLANS',
      icon: 'fa fa-sitemap',
      uiUrl: '/ai/change-plans',
      order: 2,
      isVisible: true,
    },
    {
      // El consumo de todas las plataformas, repartido por plataforma. El de cada inquilino se mira
      // dentro de su plataforma, en `ai-admin/usage`.
      id: 'ai-system-usage-platforms',
      name: 'BACKOFFICE.MENU.AI_USAGE_PLATFORMS',
      icon: 'fa fa-chart-pie',
      uiUrl: '/ai/usage-platforms',
      order: 2,
      isVisible: true,
    },
    {
      // Global: un solo servicio de IA y una sola lista de precios para todas las plataformas.
      id: 'ai-system-pricing',
      name: 'BACKOFFICE.MENU.AI_PRICING',
      icon: 'fa fa-dollar-sign',
      uiUrl: '/ai/pricing',
      order: 3,
      isVisible: true,
    },
    {
      id: 'ai-system-agents',
      name: 'BACKOFFICE.MENU.AI_AGENTS',
      icon: 'fa fa-robot',
      uiUrl: '/ai/agents',
      order: 4,
      isVisible: true,
    },
    {
      id: 'ai-system-skills',
      name: 'BACKOFFICE.MENU.AI_SKILLS',
      icon: 'fa fa-puzzle-piece',
      uiUrl: '/ai/skills',
      order: 5,
      isVisible: true,
    },
    {
      id: 'ai-system-tiers',
      name: 'BACKOFFICE.MENU.AI_TIERS',
      icon: 'fa fa-layer-group',
      uiUrl: '/ai/tiers',
      order: 6,
      isVisible: true,
    },
    {
      id: 'ai-system-mcp-tools',
      name: 'BACKOFFICE.MENU.AI_MCP_TOOLS',
      icon: 'fa fa-plug',
      uiUrl: '/ai/mcp-tools',
      order: 7,
      isVisible: true,
    },
    {
      id: 'ai-system-routing',
      name: 'BACKOFFICE.MENU.AI_ROUTING',
      icon: 'fa fa-random',
      uiUrl: '/ai/routing',
      order: 8,
      isVisible: true,
    },
    {
      id: 'ai-system-assets',
      name: 'BACKOFFICE.MENU.AI_ASSETS',
      icon: 'fa fa-file-alt',
      uiUrl: '/ai/assets',
      order: 9,
      isVisible: true,
    },
    {
      id: 'ai-system-knowledge',
      name: 'BACKOFFICE.MENU.AI_KNOWLEDGE',
      icon: 'fa fa-book',
      uiUrl: '/ai/knowledge',
      order: 10,
      isVisible: true,
    },
  ],
};

/**
 * Menu entries that need a surface of the platform's services, keyed by menu id. The values are the
 * `PlatformSurfaces` codes of the BFF probe (`API/Services/PlatformCapabilityProbe.cs`).
 *
 * The keys are also the first route segment of each screen, which is what `switchPlatform` relies on.
 */
const MENU_SURFACES: Readonly<Record<string, string>> = {
  'dynamic-view': 'dynamic-views',
  'dynamic-studio': 'dynamic-studio',
};

/**
 * The platform's settings in the console catalogue, pinned to the end of the platform menu.
 *
 * Without it, reaching the wiring or OAuth of the platform being operated meant going back to the
 * launcher, then the catalogue, then finding it. Visible to everyone: the catalogue degrades to read-only
 * without console capabilities. Absolute URL, so it is added after `withPlatform()`.
 */
function platformSettingsMenu(platformCode: string): IMenu {
  return {
    id: 'platform-settings',
    name: 'BACKOFFICE.MENU.PLATFORM_SETTINGS',
    icon: 'fa fa-sliders-h',
    uiUrl: `/platforms/${platformCode}/general`,
    order: 99,
    isVisible: true,
  };
}

/**
 * Menú del **primer nivel**: lo que no pertenece a ninguna plataforma.
 *
 * El catálogo de plataformas y la definición global del servicio de IA. Es una lista corta a
 * propósito: en cuanto algo se administra "de una plataforma" —seguridad, control de código,
 * artefactos, la IA de un inquilino— su sitio es el menú de la plataforma, y subirlo aquí obligaría a
 * explicar sobre cuál actúa.
 */
export const COMMON_MENU: IMenu[] = [
  // Top-down, as the model nests (docs/PLATAFORMAS-APLICACIONES-IDPS.md §11): the IdP, its groups, the common
  // services groups pick from, the platforms; then the system's AI and, last, operators (CONSOLE_OPERATORS_MENU).
  {
    // The IdP this BackOffice is bound to (read only): which environment is being operated.
    id: 'console-idp',
    name: 'BACKOFFICE.MENU.IDP',
    icon: 'fa fa-id-badge',
    uiUrl: '/console/idp',
    order: 1,
    isVisible: true,
  },
  {
    // Groups of platforms and the common services each one can use (§11).
    id: 'console-groups',
    name: 'BACKOFFICE.MENU.GROUPS',
    icon: 'fa fa-layer-group',
    uiUrl: '/console/groups',
    order: 2,
    isVisible: true,
  },
  {
    // The catalog of common services: URL, scope and secret of each shared service (§11).
    id: 'console-common-services',
    name: 'BACKOFFICE.MENU.COMMON_SERVICES',
    icon: 'fa fa-cubes',
    uiUrl: '/console/common-services',
    order: 3,
    isVisible: true,
  },
  {
    id: 'platforms',
    name: 'BACKOFFICE.MENU.PLATFORMS',
    icon: 'fa fa-th',
    uiUrl: '/platforms',
    order: 4,
    isVisible: true,
  },
];

/**
 * Quién puede gestionar el catálogo de plataformas.
 *
 * Entrada aparte y **no** dentro de `COMMON_MENU` porque esta sí depende de quien mire: la capacidad de
 * consola `operators.manage` la tienen los operadores, no todo el que entra en el BackOffice. Es la
 * única entrada del primer nivel condicionada, y por eso está fuera de la lista en lugar de llevar un
 * `isVisible` que habría que recalcular.
 */
const CONSOLE_OPERATORS_MENU: IMenu = {
  id: 'console-operators',
  name: 'BACKOFFICE.MENU.CONSOLE_OPERATORS',
  icon: 'fa fa-user-shield',
  uiUrl: '/console/operators',
  order: 7,
  isVisible: true,
};

/**
 * Integraciones de infraestructura (Azure DevOps, ArgoCD) y destinos de despliegue. Condicionada como la de
 * operadores: solo para quien tenga `infra.view`.
 */
const CONSOLE_INTEGRATIONS_MENU: IMenu = {
  id: 'console-integrations',
  name: 'BACKOFFICE.MENU.INTEGRATIONS',
  icon: 'fa fa-plug',
  uiUrl: '/console/integrations',
  order: 6,
  isVisible: true,
};

/**
 * Menú del BackOffice para el perfil que lo está usando y la plataforma en la que está.
 *
 * Sin plataforma abierta (lanzador, catálogo) se publica el menú común: enseñar el de una plataforma
 * cuando no hay ninguna elegida invita a entrar en pantallas que no sabrían contra qué producto
 * trabajan.
 *
 * Devuelve SIEMPRE objetos nuevos y no muta `BACKOFFICE_MENU`. No es cosmético: esos objetos están
 * enlazados a la vez en la barra superior y en el sidebar, y reescribirlos en el sitio es lo que
 * provocaba NG0100 dentro de la detección de cambios —y con ello la pantalla congelada— en el layout
 * de Activity (ver §9.4 de ANGULAR-21-MIGRATION-APPS.md en la UILib).
 */
/**
 * What each platform entry asks of a console operator who is not the root (docs/OPERADOR-CONTENIDO.md): any of the
 * listed capabilities on the platform. Entries not listed are infrastructure: provision. A platform session or the root
 * global admin are not filtered by this.
 */
export const MENU_CAPABILITIES: Readonly<Record<string, readonly string[]>> = {
  overview: [ConsoleCapability.ViewPlatforms],
  'dynamic-view': [ConsoleCapability.PlatformContent, ConsoleCapability.ProvisionPlatforms],
  reports: [ConsoleCapability.PlatformContent, ConsoleCapability.ProvisionPlatforms],
  themes: [ConsoleCapability.PlatformContent, ConsoleCapability.UpdatePlatforms],
  maintenance: [ConsoleCapability.ManageMaintenance],
  'ai-usage': [ConsoleCapability.AiUsage, ConsoleCapability.ProvisionPlatforms],
  'security-roles': [ConsoleCapability.PlatformRoles],
  'security-users': [ConsoleCapability.PlatformUsers],
  'security-menu': [ConsoleCapability.PlatformMenus],
  tenants: [ConsoleCapability.PlatformTenants, ConsoleCapability.ProvisionPlatforms],
};

/** Keeps the entries whose capability `allows`, and the groups that keep some child. */
function withCapabilities(items: IMenu[], allows: (capabilities: readonly string[]) => boolean): IMenu[] {
  return items
    .map((item) => (item.children ? { ...item, children: withCapabilities(item.children, allows) } : item))
    .filter((item) => (item.children
      ? item.children.length > 0
      : allows(MENU_CAPABILITIES[item.id ?? ''] ?? [ConsoleCapability.ProvisionPlatforms])));
}

export function backofficeMenu(
  isAdmin: boolean,
  platformCode: string | null,
  canManageOperators = false,
  missingSurfaces: ReadonlySet<string> = new Set(),
  canOpenSettings = false,
  canViewInfra = false,
  isRoot = false,
  /** A console operator who is not the root: which capabilities they hold on `platformCode`. Null: not filtered. */
  operatorAllows: ((capabilities: readonly string[]) => boolean) | null = null,
  /** Console infrastructure (IdP, groups, common services): only for whoever administers platforms. */
  canSeeConsoleInfra = true,
): IMenu[] {
  if (!platformCode) {
    // La gestión de operadores solo para quien pueda: la capacidad no se deduce de ser administrador
    // —un administrador global de una plataforma NO es operador de la consola, que es justo la
    // distinción que el eje nuevo introduce— así que llega como parámetro y no de `isAdmin`.
    // The system's AI only for the root global admin (2026-11-02): an operator with only «view platforms» had every
    // entry, and the AI service takes any console token as a platform caller. The BFF also answers 403.
    // Console infrastructure only for whoever administers (docs/OPERADOR-CONTENIDO.md): a content operator keeps the
    // catalogue of platforms and nothing else.
    const common = canSeeConsoleInfra ? COMMON_MENU : COMMON_MENU.filter((item) => item.id === 'platforms');
    return [
      ...common,
      ...(isRoot ? [AI_SYSTEM_MENU] : []),
      ...(canViewInfra ? [CONSOLE_INTEGRATIONS_MENU] : []),
      ...(canManageOperators ? [CONSOLE_OPERATORS_MENU] : []),
    ];
  }

  // A console operator who is not the root gets every section, each entry filtered by its capability; before, the
  // console row's isAdmin decided and an operator never saw security.
  const items = operatorAllows
    ? withCapabilities([...BACKOFFICE_MENU, SECURITY_MENU, TENANTS_MENU, APPLICATIONS_MENU], operatorAllows)
    : isAdmin
      ? [...BACKOFFICE_MENU, SECURITY_MENU, TENANTS_MENU, APPLICATIONS_MENU]
      : [...BACKOFFICE_MENU];

  // The settings entry only for whoever manages this platform: for anyone else the catalogue has no
  // settings to show for it.
  const withSettings = withPlatform(withoutMissingSurfaces(items, missingSurfaces), platformCode);
  return canOpenSettings ? [...withSettings, platformSettingsMenu(platformCode)] : withSettings;
}

/**
 * Drops the entries whose surface no service of the platform serves (see `PlatformCapabilitiesService`
 * for why this is fail-open), and the groups left empty by that.
 */
function withoutMissingSurfaces(items: IMenu[], missing: ReadonlySet<string>): IMenu[] {
  if (!missing.size) return items;

  return items
    .filter((item) => !(item.id && MENU_SURFACES[item.id] && missing.has(MENU_SURFACES[item.id])))
    .map((item) => (item.children ? { ...item, children: withoutMissingSurfaces(item.children, missing) } : item))
    .filter((item) => !item.children || item.children.length > 0);
}

/**
 * Antepone la plataforma a los `uiUrl` del menú, que se declaran relativos.
 *
 * Las entradas sin `uiUrl` —las que solo agrupan— se dejan como están: darles una dirección haría
 * navegable un rótulo que no lleva a ninguna pantalla.
 */
function withPlatform(items: IMenu[], platformCode: string): IMenu[] {
  return items.map((item) => ({
    ...item,
    uiUrl: item.uiUrl ? `/p/${platformCode}/${item.uiUrl}` : item.uiUrl,
    children: item.children ? withPlatform(item.children, platformCode) : item.children,
  }));
}

/**
 * Resuelve los `name` del menú, que son claves de traducción.
 *
 * El menú del BackOffice se define aquí (Identity no lo sirve, ver `subscribeMenus`) y sus rótulos
 * estaban escritos en castellano a pelo, así que cambiar de idioma no los tocaba. Y no se puede
 * traducir en plantilla: el sidebar y la barra superior pintan `IMenu.name` tal cual, sin pipe.
 *
 * Devuelve un árbol NUEVO y no muta las constantes: son objetos compartidos por la barra y el sidebar,
 * y reescribirlos en el sitio es el NG0100 que congela la pantalla (ver `publishMenu`).
 */
export function translateMenu(items: IMenu[], resolve: (key: string) => string): IMenu[] {
  return items.map((item) => ({
    ...item,
    name: item.name ? resolve(item.name) : item.name,
    children: item.children ? translateMenu(item.children, resolve) : item.children,
  }));
}

/**
 * Iconos con los que `OveraLayoutComponent.calculateMenubarItems()` marca las dos entradas que hay que
 * localizar en la barra superior: el menú de usuario y, dentro, el desplegable de seguridad que aquí se
 * retira. Son literales de la librería; se nombran para que el filtro diga qué busca y por qué.
 */
const USER_MENU_ICON = 'pi pi-user';
const SECURITY_MENU_ICON = 'pi pi-shield';
const PROFILE_MENU_ICON = 'pi pi-id-card';

/** sessionStorage key: the AI assistant window is open (survives a reload in the same session). */
const AI_CHAT_OPEN_KEY = 'backoffice-ai-chat-open';
/** localStorage key: the user hid the assistant's floating button (brought back from the user menu). */
const AI_CHAT_LAUNCHER_HIDDEN_KEY = 'backoffice-ai-chat-launcher-hidden';

/** `AiChat` section of the UI appsettings, camel-cased (see `AiChatSettings.cs`). */
interface AiChatConfig {
  enabled?: boolean;
  agentName?: string;
  title?: string;
  brandIcon?: string;
  welcomeText?: string;
  placeholder?: string;
  launcherLabel?: string;
  quickActions?: { label?: string; text?: string; icon?: string }[] | null;
  tier?: string;
  tierSelectable?: boolean;
  showReasoning?: boolean;
  showUsage?: boolean;
  thinkLonger?: boolean;
  suggestScreens?: boolean;
}

/** Chips of an empty conversation when `AiChat:QuickActions` is not configured. */
const DEFAULT_AI_CHAT_QUICK_ACTIONS = [
  { label: 'BACKOFFICE.AI_CHAT.QA_PLATFORMS', icon: 'pi pi-sitemap' },
  { label: 'BACKOFFICE.AI_CHAT.QA_ROLE', icon: 'pi pi-shield' },
  { label: 'BACKOFFICE.AI_CHAT.QA_MENU', icon: 'pi pi-bars' },
  { label: 'BACKOFFICE.AI_CHAT.QA_SCRIPTS', icon: 'pi pi-code' },
];

@Component({
    selector: 'app-layout',
    templateUrl: './layout.component.html',
    styleUrls: ['./layout.component.scss'],
    standalone: false
})
export class LayoutComponent extends OveraLayoutComponent implements OnInit, AfterViewInit {
  static override className = 'LayoutComponent';

  DeviceType = DeviceType;

  /**
   * Local property initialized eagerly so the sidebar always gets items.
   *
   * Arranca con el menú común —el del primer nivel— porque al construir el componente no hay ninguna
   * plataforma abierta todavía: la fija el guard de la ruta. `publishMenu()` republica en cuanto haya
   * plataforma y en cuanto se carguen los permisos del usuario.
   *
   * Se traduce ya aquí para no pintar las claves crudas en el primer fotograma. Si las traducciones
   * todavía no han llegado —`setTranslations()` las pide desde el constructor del AppModule, así que
   * resuelven DESPUÉS del primer pintado— saldrán las claves, y el `rebuildOnLanguageChange` de abajo
   * las corrige en cuanto lleguen: ese `onLangChange` también se emite en la primera carga.
   */
  sidebarMenuItems: IMenu[] = translateMenu(COMMON_MENU, (key) => this.translate.get(key));

  /**
   * What the top bar gets on desktop: the current menu plus the other levels, for its search box.
   *
   * The top bar builds both its search and its responsive menu from the same input, so the extended
   * tree is handed over only on desktop, where the search is drawn and the responsive menu is not (see
   * the template). Temporary until the UILib top bar takes the search items separately.
   */
  searchMenuItems: IMenu[] = this.sidebarMenuItems;

  isSidebarCollapsed = true;
  activeRoute = '';
  breadcrumbItems: OveraMenuItem[] = [];
  isGoldenLayoutView = false;

  private readonly deviceDetectorService = inject(DeviceDetectorService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly layoutModeService = inject(LayoutModeService);
  private readonly platformService = inject(PlatformService);
  private readonly consoleService = inject(ConsoleService);
  private readonly capabilitiesService = inject(PlatformCapabilitiesService);
  private readonly identityLinks = inject(IdentityLinksService);
  private readonly injector = inject(Injector);
  /** Width taken by an open AI assistant drawer; see AiDrawerSpaceService. */
  readonly aiDrawer = inject(AiDrawerSpaceService);

  deviceType: DeviceType = DeviceType.Desktop;

  /**
   * Room kept free for the AI drawer. Desktop only: on phones and tablets the drawer takes almost
   * the whole width, and squeezing the content into what is left is worse than covering it.
   */
  get aiDrawerReservedWidth(): number {
    return this.deviceType === DeviceType.Desktop ? this.aiDrawer.width() : 0;
  }

  //START SECTION AI CHAT

  /**
   * Asistente general de IA de la consola, de la sección `AiChat` del appsettings de la UI (ver
   * `AiChatSettings.cs`). Sin `AgentName`, o con `Enabled=false`, no hay asistente. El agente
   * (`backoffice-assistant`) tiene las mismas tools que un cliente MCP externo; el modelo y el gasto los
   * decide el servicio de IA con el plan del inquilino.
   */
  readonly aiChat: AiChatConfig = this.config.getModuleConfig('overa-components', 'aiChat') ?? {};

  readonly chatMicEnabled: boolean = this.config.getModuleConfig('overa-components', 'aiChatMicEnabled') ?? false;

  get aiChatEnabled(): boolean {
    return this.aiChat.enabled === true && !!this.aiChat.agentName;
  }

  /** The panel is drawn: assistant configured and a signed-in user (the panel needs one). */
  get aiChatVisible(): boolean {
    return this.aiChatEnabled && !!this.accountService.currentUser;
  }

  /** Tokens and tier under each answer only where configured; the cost, to admins only. */
  get aiChatShowUsageCost(): boolean {
    return this.aiChat.showUsage === true && (this.accountService.currentUser?.isAdmin ?? false);
  }

  /** i18n key or literal: `translate.get` returns the text itself when it is not a key. */
  get aiChatTitle(): string {
    return this.translate.get(this.aiChat.title || 'BACKOFFICE.AI_CHAT.TITLE');
  }

  get aiChatPlaceholder(): string {
    return this.translate.get(this.aiChat.placeholder || 'BACKOFFICE.AI_CHAT.PLACEHOLDER');
  }

  get aiChatLauncherLabel(): string {
    return this.translate.get(this.aiChat.launcherLabel || 'BACKOFFICE.AI_CHAT.LAUNCHER_LABEL');
  }

  /**
   * Bienvenida con el nombre de pila. El marcador `{{name}}` se sustituye también a mano por si la
   * traducción llega compilada por MessageFormat, que ignora `{{...}}`.
   */
  get aiChatWelcomeText(): string {
    const user = this.accountService.currentUser;
    const name = user?.firstName || user?.userName || '';
    const text = this.translate.get(this.aiChat.welcomeText || 'BACKOFFICE.AI_CHAT.WELCOME_TEXT', undefined, { name });
    return text.replace(/\{\{\s*name\s*\}\}/g, name);
  }

  // Referencia ESTABLE: el panel repinta los chips en cada ciclo de detección de cambios si el
  // array cambia de identidad, y el botón recreado bajo el puntero se come el clic.
  private _aiChatQuickActions?: { label: string; text: string; icon: string }[];

  /** Chips de arranque (`AiChat:QuickActions`); sin configurar, los de `BACKOFFICE.AI_CHAT.QA_*`. */
  get aiChatQuickActions(): { label: string; text: string; icon: string }[] {
    if (this._aiChatQuickActions) return this._aiChatQuickActions;
    const configured = Array.isArray(this.aiChat.quickActions) ? this.aiChat.quickActions : DEFAULT_AI_CHAT_QUICK_ACTIONS;
    const valid = configured.filter((a): a is { label: string; text?: string; icon?: string } => !!a?.label);
    const isKey = (v: string) => /^[A-Z0-9_-]+(\.[A-Z0-9_-]+)+$/.test(v);
    // Translations not loaded yet: retry on the next cycle instead of caching raw keys.
    if (valid.some((a) => isKey(a.label) && this.translate.get(a.label) === a.label)) return [];
    this._aiChatQuickActions = valid.map((a) => ({
      label: this.translate.get(a.label),
      text: this.translate.get(a.text || a.label),
      icon: a.icon || 'pi pi-comment',
    }));
    return this._aiChatQuickActions;
  }

  /**
   * Dónde está trabajando la persona: la plataforma abierta, su aplicación y su inquilino. El agente lo usa
   * como valor por omisión de `platform`/`tenant`/`microService`. El panel solo reenvía la sección cuando
   * cambia, y `computed` mantiene la referencia mientras no cambie.
   */
  readonly aiChatContextSections = computed<AiContextSection[]>(() => {
    const platform = this.platformService.active();
    if (!platform) {
      return [{ label: 'BackOffice', content: 'Console level: no platform is open. Ask for the platform when a tool needs one.' }];
    }
    const lines = [`Open platform: ${platform.code} (${platform.name})`];
    const tenant = this.platformService.activeTenant();
    if (tenant) lines.push(`Default tenant of the platform: ${tenant.canonicalName}`);
    const application = this.platformService.activeApplication();
    if (application) lines.push(`Selected application: ${application.code} (microService: ${application.serviceKey})`);
    return [{ label: 'BackOffice', content: lines.join('\n') }];
  });

  /**
   * Screens the agent may suggest (`ui_suggest_screen`), as buttons below its answer: the menu of the open platform,
   * the console's and the tabs of each platform's settings (plans, applications…), which the menu does not list.
   * Rebuilt by `publishMenu`, and replaced only when it changes so the panel keeps the same reference.
   */
  aiChatNavigationTargets: AiNavigationTarget[] = [];

  private buildAiChatNavigationTargets(platformMenu: IMenu[], consoleMenu: IMenu[]): AiNavigationTarget[] {
    if (!this.aiChatEnabled || this.aiChat.suggestScreens === false) return [];
    const targets: AiNavigationTarget[] = [];
    const seen = new Set<string>();
    const add = (label: string, url: string) => {
      if (!label || !url || seen.has(url)) return;
      seen.add(url);
      targets.push({ id: url, label, url });
    };
    const walk = (items: IMenu[], path: string[]) => {
      for (const m of items ?? []) {
        if (m.isVisible === false) continue;
        const name = (m.name ?? '').trim();
        if (m.uiUrl) add([...path, name].join(' › '), m.uiUrl.startsWith('/') ? m.uiUrl : `/${m.uiUrl}`);
        if (m.children?.length) walk(m.children as IMenu[], name ? [...path, name] : path);
      }
    };
    const active = this.platformService.active();
    if (active) walk(platformMenu, [this.platformService.label(active)]);
    walk(consoleMenu, [this.translate.get('BACKOFFICE.PLATFORM_SWITCHER.CONSOLE')]);
    for (const p of this.platformService.available()) {
      const settings = [this.platformService.label(p), this.translate.get('BACKOFFICE.MENU.PLATFORM_SETTINGS')];
      for (const tab of WIRING_TABS) add([...settings, this.translate.get(WIRING_TAB_LABELS[tab])].join(' › '), `/platforms/${p.code}/${tab}`);
    }
    return targets;
  }

  /** A screen suggested by the agent. */
  onAiChatNavigate(target: AiNavigationTarget): void {
    if (this.router.url !== target.url) this.router.navigateByUrl(target.url);
  }

  /** The window is open. Kept in sessionStorage so that it is still open after a reload. */
  get aiChatOpen(): boolean {
    return this._aiChatOpen;
  }
  set aiChatOpen(open: boolean) {
    this._aiChatOpen = open;
    try { sessionStorage.setItem(AI_CHAT_OPEN_KEY, open ? '1' : '0'); } catch { /* storage unavailable */ }
  }
  private _aiChatOpen = (() => {
    try { return sessionStorage.getItem(AI_CHAT_OPEN_KEY) === '1'; } catch { return false; }
  })();

  /** The user hid the floating button from the chat's + menu. */
  get aiChatLauncherHidden(): boolean {
    return this._aiChatLauncherHidden;
  }
  set aiChatLauncherHidden(hidden: boolean) {
    this._aiChatLauncherHidden = hidden;
    try { localStorage.setItem(AI_CHAT_LAUNCHER_HIDDEN_KEY, hidden ? '1' : '0'); } catch { /* storage unavailable */ }
  }
  private _aiChatLauncherHidden = (() => {
    try { return localStorage.getItem(AI_CHAT_LAUNCHER_HIDDEN_KEY) === '1'; } catch { return false; }
  })();

  override ngOnInit(): void {
    super.ngOnInit();

    this.deviceDetectorService.deviceType$.subscribe((deviceType) => {
      this.deviceType = deviceType;
      this.cdr.markForCheck();
    });

    this.layoutModeService.isGoldenLayout$.subscribe((isGolden) => {
      this.isGoldenLayoutView = isGolden;
    });

    // Contador de la campana: al arrancar, cuando Notification lo recalcula y cuando pide refrescar.
    this.refreshNotificationsCount();
    this.notificationService.notificationsNumber.subscribe((count: number) => this.setNotificationsCircle(count));
    this.notificationService.refreshNotification.subscribe(() => this.refreshNotificationsCount());

    // Fin de un trabajo de Jobs que sigue un alta de app (clave app-delivery:…, API/Infra/AppDeliveryWatch.cs): si en el
    // chat hay una comprobación programada con esa clave, se hace ya. El aviso en sí (toast y campana) lo pinta
    // @overa/notification.
    this.injector.get(NotificationService, null)?.newNotification.subscribe((notification: { text?: string } | null) => {
      const key = LayoutComponent.jobKeyOf(notification?.text);
      if (key?.startsWith('app-delivery:')) {
        this.aiChatWatchSignal = { key, token: Date.now() };
        this.cdr.markForCheck();
      }
    });
  }

  /** Comprobación del chat que adelanta un aviso de fin (`[watchSignal]` de overa-ai-chat-panel). */
  aiChatWatchSignal: { key: string; token: number } | null = null;

  /** La clave (`Key`) del JSON de texto de un aviso de Jobs, o null. */
  static jobKeyOf(text: string | undefined | null): string | null {
    if (!text) return null;
    try {
      const parsed = JSON.parse(text);
      return typeof parsed?.Key === 'string' ? parsed.Key : null;
    } catch {
      return null;
    }
  }

  /**
   * Prevent Identity server from overwriting the Backoffice static menu.
   *
   * El menú de Identity es global y compartido por todas las aplicaciones (`MenuItem` con
   * habilitación por tenant), así que el del BackOffice se define aquí. Lo que sí se escucha es el
   * evento de permisos: es lo único que decide si aparece la sección de Seguridad, y llega después de
   * que se construya el componente.
   */
  override subscribeMenus(): void {
    this.publishMenu();

    // Las capacidades de consola se piden aquí, y tiene que ser aquí: el guard de `/console/operators`
    // también las pide, pero a esa dirección no se llega si la entrada del menú no aparece. Sin esta
    // llamada la sección de consola no existiría nunca. La respuesta cambia la señal y el efecto de
    // más abajo republica el menú solo.
    //
    // Sin `takeUntil`: la petición se comparte en el servicio (`shareReplay`) y su resultado es estado
    // de la sesión, no de este componente. Cancelarla al destruir el layout dejaría al siguiente sin
    // capacidades y sin nadie que las volviera a pedir.
    this.consoleService.load().subscribe();

    this.accountService
      .getUserPermissionsChangedEvent()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => this.publishMenu());

    // Los rótulos del menú se resuelven en TypeScript, así que quedan congelados en el idioma que
    // hubiera al construirlos: sin esto, cambiar de idioma dejaba TODO el menú en castellano. Cubre
    // además la primera carga, porque `onLangChange` también se emite cuando llegan las traducciones
    // iniciales —que resuelven después del primer pintado— y entonces se pintaban las claves crudas.
    //
    // Se suscribe a mano en vez de usar `rebuildOnLanguageChange`: ese ayudante llama a `inject()` por
    // dentro y solo vale en contexto de inyección, y esta clase no declara constructor propio (lo
    // heredaría con la docena de parámetros de la base). Es el mismo patrón que usa `OveraLayoutComponent`
    // para rehacer el menubar.
    this.translate.onLangChange
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => this.publishMenu());

    // La plataforma decide QUÉ menú se publica: el común en el primer nivel y el de la plataforma
    // dentro de ella. La fija `platformGuard` a partir de la URL, así que el menú cambia con la
    // navegación —incluida la de "atrás"— sin que nadie tenga que avisarlo.
    //
    // Con `injector` explícito porque esto no corre en contexto de inyección: esta clase no declara
    // constructor propio (lo heredaría con la docena de parámetros de la base), que es el mismo motivo
    // por el que arriba se escucha `onLangChange` a mano.
    // La primera ejecución de un efecto es inmediata y no es un cambio de plataforma: solo publica el
    // menú. La barra superior la construye la clase base durante su propia inicialización, y
    // rehacerla aquí antes de tiempo es meter una excepción dentro de la detección de cambios — que en
    // Angular 21 no se ve como un error, se ve como la pantalla congelada.
    //
    // The platforms that can be opened count too: they are the options of the platform switcher in the
    // top bar, and the catalogue usually arrives after the first run.
    let previousKey: string | undefined;
    effect(() => {
      const code = this.platformService.active()?.code ?? null;
      const reachable = this.platformService.available().map((p) => p.code).join(',');
      const key = `${code}|${reachable}`;
      const isFirstRun = previousKey === undefined;
      const changed = !isFirstRun && key !== previousKey;
      previousKey = key;

      this.publishMenu();
      if (changed) this.calculateMenubarItems();
      // The first run too, but after the base class has built the bar: the platform or the catalogue may
      // already be there, and the bar built before them had no platform switcher until the next change.
      else if (isFirstRun) setTimeout(() => this.calculateMenubarItems());
    }, { injector: this.injector });
  }

  /**
   * Publica el menú que corresponde al usuario actual.
   *
   * Una sola asignación por destino y con un array nuevo: la barra y el sidebar tienen enlazado lo
   * que se les pasa, y cambiarlo por debajo —en vez de sustituirlo— es lo que dispara NG0100.
   */
  private publishMenu(): void {
    const user = this.accountService.currentUser;
    const isAdmin = !!(user?.isGlobalAdmin || user?.isAdmin);
    const code = this.platformService.active()?.code ?? null;
    const resolve = (key: string) => this.translate.get(key);
    // Se lee la señal aquí dentro a propósito: esto se llama desde el efecto de abajo, así que
    // cuando la respuesta de Identity llega y la señal cambia, el menú se vuelve a publicar solo.
    // Sin eso, la entrada de operadores aparecería en la siguiente navegación y no al entrar.
    const canManageOperators = this.consoleService.can(ConsoleCapability.ManageOperators);
    const menuFor = (platformCode: string | null) =>
      translateMenu(
        backofficeMenu(
          isAdmin,
          platformCode,
          canManageOperators,
          // Read here too, for the same reason: the menu is rebuilt when the probe answers.
          platformCode ? this.capabilitiesService.missingSurfaces(platformCode) : new Set<string>(),
          this.consoleService.manages(platformCode),
          this.consoleService.can(ConsoleCapability.InfraView),
          this.consoleService.isRoot(),
          platformCode && !sessionPlatform() && !this.consoleService.isRoot()
            ? (caps) => caps.some((c) => this.consoleService.canFor(c as ConsoleCapabilityValue, platformCode))
            : null,
          this.consoleService.isRoot() || this.consoleService.managesAny(),
        ),
        resolve,
      );

    const items = menuFor(code);

    this.menuItems = items;
    this.sidebarMenuItems = items;
    this.searchMenuItems = this.searchTree(items, code, menuFor);

    const targets = this.buildAiChatNavigationTargets(code ? items : [], menuFor(null));
    if (JSON.stringify(targets) !== JSON.stringify(this.aiChatNavigationTargets)) this.aiChatNavigationTargets = targets;
  }

  /**
   * The menus of every level, grouped under the platform they belong to, for the top bar search.
   *
   * Searching "Users" from the console used to find nothing, although every platform has Users. The
   * search shows the path of each result ("Harley/Security/Users"), so grouping by platform is what tells
   * the results apart. The current platform is grouped too, so its results read the same way.
   */
  private searchTree(
    items: IMenu[],
    code: string | null,
    menuFor: (platformCode: string | null) => IMenu[],
  ): IMenu[] {
    const group = (id: string, name: string, children: IMenu[]): IMenu => ({
      id: `search-${id}`,
      name,
      order: 0,
      isVisible: true,
      children,
    });

    const platforms = this.platformService.available();
    const active = platforms.find((p) => p.code === code);
    const current = code ? [group(code, active ? this.platformService.label(active) : code, items)] : items;
    const console = code
      ? [group('console', this.translate.get('BACKOFFICE.PLATFORM_SWITCHER.CONSOLE'), menuFor(null))]
      : [];
    const others = platforms
      .filter((p) => p.code !== code)
      .map((p) => group(p.code, this.platformService.label(p), menuFor(p.code)));

    return [...current, ...console, ...others];
  }

  /**
   * Retira de la barra superior lo que en el BackOffice no debe estar: el selector de tenant y el
   * desplegable "Seguridad" del menú de usuario.
   *
   * Los dos sobran por el mismo motivo de fondo: **el ámbito es propiedad de cada pantalla**. La zona
   * de seguridad elige su tenant, lo deja a la vista y lo restaura al salir; el resto del BackOffice no
   * trabaja dentro de un tenant. Un mando global que conmuta el tenant de la SESIÓN sobre lo que haya
   * abierto, o unos atajos a `core/security/*` que actúan sobre ese mismo tenant de sesión, son una
   * segunda puerta con reglas distintas — la forma de acabar administrando el tenant equivocado.
   *
   * Se posprocesa lo que publica la clase base en vez de reimplementarla: así el selector de idioma, el
   * menú de usuario y los iconos de navegación siguen viniendo de la librería y no se pierde lo que
   * añada en el futuro. El desplegable de Seguridad se localiza por el icono, que es lo único estable
   * que trae la entrada; si la librería lo cambiara, reaparecería — un fallo visible, no silencioso. El
   * de tenant se localiza por identidad; ver `isTenantSelector`.
   */
  override calculateMenubarItems(): void {
    super.calculateMenubarItems();

    // Se reconstruye publicando objetos nuevos, sin tocar los que la barra ya tiene enlazados: mutar
    // en el sitio lo que está pintado es lo que dispara NG0100 dentro de la detección de cambios.
    const items = this.menubarItems
      .filter((item) => !this.isTenantSelector(item))
      .map((item) => {
        if (item?.icon !== USER_MENU_ICON) return item;

        const children = item.items ?? [];
        const withoutSecurity = children.filter((child) => child?.icon !== SECURITY_MENU_ICON);

        return { ...item, items: this.withAiChatItem(this.withPasskeysItem(withoutSecurity)) };
      });

    const launcher = this.launcherItem();
    // La campana va justo antes del menú de usuario, como en las apps.
    const userIndex = items.findIndex((item) => item?.icon === USER_MENU_ICON);
    items.splice(userIndex < 0 ? items.length : userIndex, 0, this.notificationsItem());
    // Sin selector de aplicación: no interesa en la consola. Cada plataforma abre la suya (`setActive`: la
    // última elegida o la de su API).
    this.menubarItems = [launcher, ...items].filter((item): item is OveraMenuItem => !!item);
    // El contador es un nodo que pone setNotificationsCircle a mano: tras repintar la barra hay que volver a ponerlo.
    setTimeout(() => this.setNotificationsCircle(this.countNotifications ?? 0));
  }

  /**
   * La campana de las notificaciones de la consola (avisos de Jobs, p. ej. el fin del alta de una app): el contador
   * de no leídas y la pantalla core/notifications de @overa/notification. Las apps la reciben como navicon de su menú;
   * el BackOffice compone su barra y la añade aquí.
   */
  private notificationsItem(): OveraMenuItem {
    return {
      icon: 'pi pi-bell',
      cssClass: 'notification-icon',
      tooltip: this.translate.get('LIB-COMMON.notifications', 'Notificaciones'),
      command: () => {
        this.router.navigateByUrl('core/notifications', {
          state: { title: this.translate.get('LIB-COMMON.notifications', 'Notificaciones') },
          info: { componentType: NotificationComponent },
        });
      },
    } as OveraMenuItem;
  }

  /** No leídas para el contador de la campana; sin Notification, la campana se queda sin número. */
  private refreshNotificationsCount(): void {
    this.notificationService
      .getCount(NOTIFICATION_TYPE.Internal)
      .pipe(catchError(() => of(0)))
      .subscribe((count: number) => this.setNotificationsCircle(count));
  }

  /**
   * "My access keys" in the user menu, right after the profile: the IdP's own page, where the signed-in
   * person adds another key or revokes one. Nothing else linked to it, so a global admin who entered with
   * their first key had no way to register the second one.
   */
  private withPasskeysItem(children: OveraMenuItem[]): OveraMenuItem[] {
    if (!this.identityLinks.passkeysUrl()) return children;

    const passkeys: OveraMenuItem = {
      id: 'user-passkeys',
      label: this.translate.get('BACKOFFICE.MENU.MY_PASSKEYS'),
      icon: 'pi pi-key',
      command: () => this.identityLinks.openPasskeys(),
    };
    const profile = children.findIndex((child) => child?.icon === PROFILE_MENU_ICON);

    return [...children.slice(0, profile + 1), passkeys, ...children.slice(profile + 1)];
  }

  /**
   * "AI assistant" at the end of the user menu: opens the window, and brings back the floating button if the
   * user hid it from the chat's + menu (otherwise there would be no way back to it).
   */
  private withAiChatItem(children: OveraMenuItem[]): OveraMenuItem[] {
    if (!this.aiChatEnabled) return children;

    const assistant: OveraMenuItem = {
      id: 'user-ai-assistant',
      label: this.aiChatTitle,
      icon: this.aiChat.brandIcon || 'pi pi-sparkles',
      command: () => {
        this.aiChatLauncherHidden = false;
        this.aiChatOpen = true;
      },
    };
    return [...children, assistant];
  }

  /**
   * Platform switcher in the top bar, labelled with the open platform.
   *
   * Cumple dos papeles y por eso lleva rótulo y no solo el icono: es el "cambiar de aplicación" —el
   * botón de rejilla al que la gente ya está acostumbrada— y es el **indicador de ámbito**. Saber en
   * todo momento sobre qué producto se está operando es justo lo que falta cuando el ámbito se elige
   * una vez y luego desaparece de la vista.
   *
   * It opens a menu instead of going to the launcher: switching platform used to mean the launcher, then
   * the platform (landing on its first screen), then finding the screen again. Now each platform keeps
   * the screen being looked at (`switchPlatformUrl`). The console and this platform's settings close
   * the list.
   *
   * También en el primer nivel (rotulado «Consola»): desde la consola, o desde pantallas que no son de una
   * plataforma como Notificaciones, se salta a una sin volver al lanzador. Sin plataformas que abrir no sale.
   */
  private launcherItem(): OveraMenuItem | null {
    const platform = this.platformService.active();
    const available = this.platformService.available();
    if (!platform && !available.length) return null;

    const platforms: OveraMenuItem[] = available.map((p) => ({
      id: `platform-switch-${p.code}`,
      label: this.platformService.label(p),
      // The context menu only draws icon classes; an SVG platform icon is left out rather than broken.
      icon: p.code === platform?.code ? 'pi pi-check' : /^(fa|pi)\b/.test(p.icon ?? '') ? p.icon : undefined,
      styleClass: p.code === platform?.code ? 'is-active-platform' : undefined,
      command: () => this.switchPlatform(p.code),
    }));

    return {
      id: 'platform-launcher',
      icon: 'pi pi-th-large',
      label: platform ? this.platformService.label(platform) : this.translate.get('BACKOFFICE.PLATFORM_SWITCHER.CONSOLE'),
      items: [
        ...platforms,
        { separator: true },
        {
          id: 'platform-switch-console',
          label: this.translate.get('BACKOFFICE.PLATFORM_SWITCHER.CONSOLE'),
          icon: 'pi pi-th-large',
          command: () => this.router.navigate(['/launcher']),
        },
        ...(platform && this.consoleService.manages(platform.code)
          ? [{
              id: 'platform-switch-settings',
              label: this.translate.get('BACKOFFICE.MENU.PLATFORM_SETTINGS'),
              icon: 'pi pi-sliders-h',
              command: () => this.router.navigate(['/platforms', platform.code, 'general']),
            }]
          : []),
      ],
    };
  }

  /** Opens another platform on the same screen, or on its overview if it does not offer that screen. */
  private switchPlatform(code: string): void {
    if (code === this.platformService.active()?.code) return;

    const missing = this.capabilitiesService.missingSurfaces(code);
    const unavailable = new Set(
      Object.entries(MENU_SURFACES)
        .filter(([, surface]) => missing.has(surface))
        .map(([menuId]) => menuId),
    );

    this.router.navigateByUrl(switchPlatformUrl(this.router.url, code, unavailable));
  }

  /**
   * Reconoce el desplegable de tenant que la librería cuelga de la barra superior.
   *
   * Se retira porque en el BackOffice el ámbito es **propiedad de cada pantalla**: la zona de
   * seguridad tiene su propio selector, lo deja a la vista y lo restaura al salir (ver
   * `SecurityScopeService`), y el resto de pantallas no operan dentro de un tenant. Un selector global
   * que conmuta el tenant de la SESIÓN sobre todo lo que haya abierto no aclara nada y despista: dos
   * mandos para lo mismo, con reglas distintas, es la forma de acabar administrando el tenant
   * equivocado. Es el mismo motivo por el que ya se retiraba el desplegable de Seguridad.
   *
   * Se identifica por IDENTIDAD, no por la etiqueta: sus opciones son los tenants disponibles. Un
   * `label` traducido dependería de que no cambie la clave `LIB-BASE.COMMON.TENANT`, y la posición
   * dependería de que la librería no reordene `fixedItems` — la lista de ids no depende de ninguna de
   * las dos cosas. El de idioma no colisiona: sus ids son códigos de idioma, no identificadores de
   * tenant.
   */
  private isTenantSelector(item: any): boolean {
    if (item?.type !== 'dropdown' || !item.items?.length) return false;

    const tenantIds = new Set((this.availableTenants ?? []).map((t) => t.id).filter((id) => !!id));
    if (!tenantIds.size) return false;

    return item.items.every((option: any) => tenantIds.has(option?.id));
  }

  override ngAfterViewInit(): void {
    super.ngAfterViewInit();
  }

  toggleSidebar(): void {
    this.isSidebarCollapsed = !this.isSidebarCollapsed;
  }
}
